import { useEffect, useState } from 'react';
import { WxDialog } from '../..';
import {
  CartError,
  useCartContext,
} from '../../../hooks/context/useCartContext';
import { __ } from '../../../i18n/l10n';

export const CartErrors = () => {
  const {
    verifyCart,
    cartErrors,
    cart,
    setCart,
    setCartErrors,
    serverCartItems,
    serverFetched,
  } = useCartContext();

  const [open, setOpen] = useState<boolean>(false);
  /** verify cart items each time you visit the cart */
  useEffect(() => {
    if (serverFetched) verifyCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverFetched]);

  useEffect(() => {
    cartErrors?.length > 0 && setOpen(true);
  }, [cartErrors.length]);

  const length = cartErrors?.length ?? 0;
  const cartError = length > 0 ? cartErrors[length - 1] : undefined;

  if (!cartError) return null;
  const index = cart.items.findIndex(
    (item) => item.variantId === cartError.variantId,
  );

  if (index === -1) {
    const temp = [...cartErrors];
    temp.pop();
    setCartErrors(temp);
  }
  const cartItem = cart.items[index];

  const popError = () => {
    const temp = [...cartErrors];
    temp.pop();
    setCartErrors(temp);
  };

  let error: { title: string; action: () => void; content: string };
  switch (cartError.error) {
    case CartError.quantity: {
      error = {
        title: __('Quantity unavailable')?.toUpperCase(),
        content: __(
          'We are sorry, the %1$s product is no longer available in the quantity you selected.',
          [cartError.variantName],
        ),
        action: () => {
          const serverCartItem = serverCartItems?.find(
            (item) => item.variantId === cartItem.variantId,
          );
          if (!serverCartItem) {
            return popError();
          }

          const updatedTotal =
            cart.totalMilesAmount -
            (cartItem.quantity - serverCartItem.availableQuantity) *
              cartItem.price.milesAmount;
          const updatedItem = {
            ...cartItem,
            quantity: serverCartItem.availableQuantity,
          };
          const updatedItems = cart.items.map((item, i) =>
            i === index ? updatedItem : item,
          );
          setCart({
            ...cart,
            items: updatedItems,
            totalMilesAmount: updatedTotal,
          });
          popError();
        },
      };
      break;
    }
    case CartError.price: {
      error = {
        title: __('PRICE CHANGE'),
        content: __(
          'The price of the %1$s product has changed. It will be adjusted in your cart.',
          [cartError.variantName],
        ),
        action: () => {
          const serverCartItem = serverCartItems?.find(
            (item) => item.variantId === cartItem.variantId,
          );
          if (!serverCartItem) {
            return popError();
          }

          const price = serverCartItem.priceOverwrite
            ? serverCartItem.priceOverwrite
            : serverCartItem.price;
          const updatedTotal =
            cart.totalMilesAmount -
            (cartItem.price.milesAmount - price.milesAmount) *
              cartItem.quantity;
          const updatedItem = {
            ...cartItem,
            price,
            priceBefore: serverCartItem.priceOverwrite
              ? serverCartItem.price
              : undefined,
          };
          const updatedItems = cart.items.map((item, i) =>
            i === index ? updatedItem : item,
          );
          setCart({
            ...cart,
            items: updatedItems,
            totalMilesAmount: updatedTotal,
          });
          popError();
        },
      };
      break;
    }
    case CartError.unavailable: {
      error = {
        title: __('Product unavailable'),
        content: __(
          'We are sorry, the %1$s product is no longer available. It will be removed from your cart.',
          [cartItem.name],
        ),
        action: function () {
          const updatedItems = [...cart.items];
          updatedItems.splice(index, 1);
          const updatedQty = cart.totalArticles - cart.items[index].quantity;
          const updatedTotal =
            cart.totalMilesAmount -
            cart.items[index].price.milesAmount * cart.items[index].quantity;

          setCart({
            ...cart,
            items: updatedItems,
            totalArticles: updatedQty,
            totalMilesAmount: updatedTotal,
          });
          popError();
        },
      };
      break;
    }
  }

  return (
    <>
      {cartError && (
        <WxDialog
          title={error.title ?? ''}
          content={error.content ?? ''}
          open={open}
          setOpen={setOpen}
          onClose={() => error.action()}
          noCancel
        />
      )}
    </>
  );
};
